/* eslint-disable */
/**
 * TODO - preamble
 */
import React from "react";
import { FormattedHTMLMessage, injectIntl } from "react-intl";
import { produce } from "immer";

import {
  VerificationStepsEnum,
  FirstResponderStatusDefaultMessagesEnum,
} from "../../lib/types/runtimeTypes";
import { hasFailedInstantMatch } from "../../lib/VerificationService/VerificationServiceHelpers";
import { getSafe } from "@jslib/shared/utils/getSafe";
import {
  VerificationService,
  FormSelectChoice,
  Organization,
  FirstResponderPersonalInfoViewModel,
  FirstResponderPersonalInfoResponse,
  CustomFormFieldConfig,
} from "../../lib/types/types";
import {
  getAvailableFirstResponderStatuses,
  getFieldConfigByFieldId,
  getStatusLabel,
  handleEmailOnKeyDown,
  updateFieldValidationErrorsByFieldId,
} from "../../lib/utils/stepComponentHelpers/stepComponentHelpers";

import { PhoneNumberComponent as PhoneNumber } from "../FormFields/PhoneNumber/PhoneNumberComponent";
import { FirstNameComponent as FirstName } from "../FormFields/FirstName/FirstNameComponent";
import { LastNameComponent as LastName } from "../FormFields/LastName/LastNameComponent";
import { EmailComponent as Email } from "../FormFields/Email/EmailComponent";
import { BirthDateComponent as BirthDate } from "../FormFields/BirthDate/BirthDateComponent";
import { MarketConsentWrapperComponent as MarketConsentWrapper } from "../FormFields/MarketConsentWrapper/MarketConsentWrapperComponent";
import { StatusComponent as FirstResponderStatus } from "../FormFields/Status/StatusComponent";
import { FirstResponderOrganizationComponent as FirstResponderOrganization } from "../FormFields/FirstResponderOrganization/FirstResponderOrganizationComponent";
import { PostalCodeComponent } from "../FormFields/PostalCode/PostalCodeComponent";
import { CountryComponentWrapper } from "../FormFields/Country/CountryComponentWrapper";
import { getOptions } from "../../options/options";
import { FormFieldCustom } from "../FormFields/FormFieldCustom/FormFieldCustom";
import { ExtraMessage } from "../ExtraMessage";
import { ChangeLocaleV2Component } from "../FormFields/ChangeLocale/ChangeLocaleComponent";
import { PersonalInfoWrapper } from "../PersonalInfoStepWrapper/PersonalInfoStepWrapper";
import { PersonalInfoStepSubmitButton } from "../PersonalInfoStepSubmitButton/PersonalInfoStepSubmitButton";
import { PersonalInfoHeader } from "../FormHeader/StepHeaders/PersonalInfoHeaderComponent";
import { CaptchaComponent } from "../FormFields/Captcha/CaptchaComponent";
import { blankOrganization } from "../../lib/types/empties";

interface StepFirstResponderPersonalInfoComponentProps {
  verificationService: VerificationService;
  intl: any;
}

export const StepFirstResponderPersonalInfo = ({
  intl,
  verificationService,
}: StepFirstResponderPersonalInfoComponentProps) => {
  const viewModel = verificationService.viewModel as FirstResponderPersonalInfoViewModel;
  const { fieldValidationErrors } = verificationService;
  const verificationResponse =
    verificationService.verificationResponse as FirstResponderPersonalInfoResponse;
  const failedInstantMatch = hasFailedInstantMatch(verificationResponse);
  const options = getOptions();

  const locale = viewModel.localeChoice.value;

  const updateFirstResponderViewModel = (
    key: keyof FirstResponderPersonalInfoViewModel,
    value: any,
  ) => {
    const nextState: FirstResponderPersonalInfoViewModel = produce(
      viewModel,
      (draft: FirstResponderPersonalInfoViewModel) => {
        (draft[key] as any) = value;
      },
    );
    verificationService.updateViewModel(nextState);
  };

  return (
    <PersonalInfoWrapper
      verificationService={verificationService}
      step={verificationResponse.currentStep}
    >
      <PersonalInfoHeader
        verificationService={verificationService}
        classNameIdentifier="first-responder"
      />

      <ChangeLocaleV2Component verificationService={verificationService} />
      <CountryComponentWrapper
        verificationService={verificationService}
        disabled={getFieldConfigByFieldId("country")?.disabled}
      />
      <FirstResponderStatus
        segmentId="first-responder"
        value={{
          value: viewModel.status,
          label: getStatusLabel(intl, viewModel.status, FirstResponderStatusDefaultMessagesEnum),
        }}
        isErrored={!!fieldValidationErrors.status}
        errorId="invalidFirstResponderStatus"
        disabled={getFieldConfigByFieldId("status")?.disabled}
        options={getAvailableFirstResponderStatuses(intl, verificationResponse.availableStatuses)}
        onChange={(status: FormSelectChoice) => {
          updateFirstResponderViewModel("status", status ? (status.value as string) : "");
          updateFieldValidationErrorsByFieldId(
            "status",
            status ? (status.value as string) : "",
            verificationService,
          );
        }}
      />

      <FirstResponderOrganization
        isErrored={!!fieldValidationErrors.organization}
        onChange={(choice: Organization = blankOrganization.organization) => {
          const nextState = produce(viewModel, (draft) => {
            draft.organization = {
              id: choice.id,
              idExtended: choice.idExtended,
              name: choice.name,
              source: choice.source,
            };

            if (viewModel.organization && viewModel.organization.source !== undefined) {
              draft.organization.source = viewModel.organization.source;
            }

            if (
              getSafe(() => verificationService.programTheme.openOrgSearchEnabled) &&
              getSafe(() => choice.id) === 0 &&
              getSafe(() => choice.name, "").length > 0
            ) {
              draft.postalCode = viewModel.postalCode ? viewModel.postalCode : "";
            } else if (viewModel.postalCode || viewModel.postalCode === "") {
              delete draft.postalCode;
            }
          });

          verificationService.updateViewModel(nextState);
          updateFieldValidationErrorsByFieldId("organization", choice, verificationService);
        }}
        value={(verificationService.viewModel as FirstResponderPersonalInfoViewModel).organization}
        verificationService={verificationService}
        disabled={getFieldConfigByFieldId("organization")?.disabled}
      />

      <div className="sid-names">
        <FirstName
          value={viewModel.firstName}
          isErrored={!!fieldValidationErrors.firstName}
          disabled={getFieldConfigByFieldId("firstName")?.disabled}
          onChange={(newValue) => {
            updateFirstResponderViewModel("firstName", newValue);
            updateFieldValidationErrorsByFieldId("firstName", newValue, verificationService);
          }}
        />

        <LastName
          value={viewModel.lastName}
          isErrored={!!fieldValidationErrors.lastName}
          disabled={getFieldConfigByFieldId("lastName")?.disabled}
          onChange={(newValue) => {
            updateFirstResponderViewModel("lastName", newValue);
            updateFieldValidationErrorsByFieldId("lastName", newValue, verificationService);
          }}
        />
      </div>

      <BirthDate
        errorId={fieldValidationErrors.birthDate}
        locale={locale}
        onChange={(newValue) => {
          updateFirstResponderViewModel("birthDate", newValue);
          updateFieldValidationErrorsByFieldId("birthDate", newValue, verificationService);
        }}
        value={viewModel.birthDate}
        disabled={getFieldConfigByFieldId("birthDate")?.disabled}
      />

      <Email
        value={viewModel.email}
        isErrored={!!fieldValidationErrors.email}
        explanation={
          <FormattedHTMLMessage
            id="emailExplanation"
            defaultMessage="Needed to send you your unique code"
          />
        }
        onChange={(newValue) => {
          updateFirstResponderViewModel("email", newValue);
          updateFieldValidationErrorsByFieldId("email", newValue, verificationService);
        }}
        onKeyDown={(event) => handleEmailOnKeyDown(event)}
        disabled={getFieldConfigByFieldId("email")?.disabled}
      />

      {verificationService.programTheme.isSmsNotifierConfigured ||
      verificationService.programTheme.smsLoopEnabled ? (
        <PhoneNumber
          isRequired={!!verificationService.programTheme.smsLoopEnabled}
          value={viewModel.phoneNumber}
          isErrored={!!fieldValidationErrors.phoneNumber}
          onChange={(newValue) => {
            updateFirstResponderViewModel("phoneNumber", newValue);
            updateFieldValidationErrorsByFieldId("phoneNumber", newValue, verificationService);
          }}
          selectedCountryCode={viewModel.countryChoice && viewModel.countryChoice.value}
          disabled={getFieldConfigByFieldId("phoneNumber")?.disabled}
        />
      ) : null}
      {getSafe(() => verificationService.programTheme.openOrgSearchEnabled) &&
        getSafe(() => viewModel.organization.id) === 0 &&
        getSafe(() => viewModel.organization.name, "").length > 0 && (
          <PostalCodeComponent
            isErrored={!!fieldValidationErrors.postalCode}
            onChange={(newValue) => {
              updateFirstResponderViewModel("postalCode", newValue);
              updateFieldValidationErrorsByFieldId("postalCode", newValue, verificationService);
            }}
            value={viewModel.postalCode}
            disabled={getFieldConfigByFieldId("postalCode")?.disabled}
          />
        )}

      {options.customFormFields.map((config: CustomFormFieldConfig) => (
        <FormFieldCustom config={config} verificationService={verificationService} />
      ))}

      <MarketConsentWrapper
        verificationService={verificationService}
        isErrored={!!fieldValidationErrors.marketConsentValue}
        onChange={(newValue) => {
          updateFirstResponderViewModel("metadata", {
            ...verificationService.viewModel.metadata,
            marketConsentValue: newValue,
          });
          updateFieldValidationErrorsByFieldId("marketConsentValue", newValue, verificationService);
        }}
        viewModel={viewModel}
        disabled={getFieldConfigByFieldId("marketConsentValue")?.disabled}
      />

      <ExtraMessage verificationService={verificationService} suffix="CollectAboveSubmit" />

      <CaptchaComponent
        verificationService={verificationService}
        isErrored={!!fieldValidationErrors.captchaToken}
      />

      <PersonalInfoStepSubmitButton
        step={VerificationStepsEnum.collectFirstResponderPersonalInfo}
        verificationService={verificationService}
        viewModel={viewModel}
        failedInstantMatch={failedInstantMatch}
        buttonText={
          <FormattedHTMLMessage
            id="step.personalInfo.submitButtonText"
            defaultMessage="Verify My First Responder Status"
          />
        }
      />
    </PersonalInfoWrapper>
  );
};

export const StepFirstResponderPersonalInfoComponent = injectIntl(StepFirstResponderPersonalInfo);
